// src/LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getFeaturedItems } from './api';
import { useInView } from 'react-intersection-observer';

// --- Material UI Imports ---
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
// --- REMOVED: Grid ---
// import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { styled, alpha, keyframes } from '@mui/system';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SendIcon from '@mui/icons-material/Send';

// --- NEW: Material UI Lab Imports for Timeline ---
import Timeline from '@mui/lab/Timeline';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem'; // Import named export
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
// --- END NEW IMPORTS ---


// --- Helper Function (getSteamImageUrl - no changes) ---
const getSteamImageUrl = (iconUrlPath, sizeSuffix = '/96fx96f') => {
    if (!iconUrlPath) return '';
    if (iconUrlPath.startsWith('http')) {
        if (iconUrlPath.includes('community.cloudflare.steamstatic.com/economy/image/') && !iconUrlPath.match(/\/\d+fx\d+f/)) {
            return `${iconUrlPath}${sizeSuffix}`;
        }
        return iconUrlPath;
    }
    if (iconUrlPath.startsWith('//')) {
        return `https:${iconUrlPath}${sizeSuffix}`;
    }
    if (iconUrlPath.startsWith('ttps://')) {
        return `h${iconUrlPath}${sizeSuffix}`;
    }
    return `https://community.cloudflare.steamstatic.com/economy/image/${iconUrlPath}${sizeSuffix}`;
};

// --- Keyframes for animations (no changes) ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const pulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 ${alpha('#29b6f6', 0.4)}; }
  70% { transform: scale(1.05); box-shadow: 0 0 0 10px ${alpha('#29b6f6', 0)}; }
  100% { transform: scale(1); box-shadow: 0 0 0 0 ${alpha('#29b6f6', 0)}; }
`;
const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

// --- Styled Components ---
const HeroSection = styled(Box)(({ theme }) => ({
    minHeight: '60vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(6, 2),
    background: `url('/banner.webp')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    color: theme.palette.common.white,
    animation: `${fadeIn} 1s ease-out`,
    overflow: 'hidden',
    position: 'relative', // Needed for pseudo-elements if adding more complex borders later

    // --- MODIFICATION START: Add Border/Shadow ---
    borderBottom: `4px solid ${alpha(theme.palette.primary.dark, 0.6)}`, // Example border
    boxShadow: `inset 0 0 15px 5px ${alpha(theme.palette.common.black, 0.4)}`, // Inner shadow for depth
    // --- MODIFICATION END ---

    // You could add more complex borders using ::before and ::after pseudo-elements
    // Example for a top/bottom border effect:
    // '&::before, &::after': {
    //     content: '""',
    //     position: 'absolute',
    //     left: 0,
    //     right: 0,
    //     height: '5px',
    //     background: `linear-gradient(to right, transparent, ${theme.palette.primary.main}, transparent)`,
    //     opacity: 0.7
    // },
    // '&::before': { top: 0 },
    // '&::after': { bottom: 0 },
}));

// --- REMOVED: AnimatedFeaturePaper ---
// const AnimatedFeaturePaper = ({ children, delay = 0, ...props }) => { ... };

// --- CtaButton (no changes) ---
const CtaButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.5, 4),
    fontSize: '1.2rem',
    fontWeight: 'bold',
    borderRadius: '50px',
    animation: `${pulse} 2.5s infinite ease-in-out`,
    marginTop: theme.spacing(3),
    transition: 'transform 0.2s ease, background-color 0.2s ease',
    // Default hover is handled by sx prop now if background is overridden
    // '&:hover': {
    //     animation: 'none',
    //     transform: 'scale(1.05)',
    // }
}));

// --- Featured Items Carousel Component (no changes) ---
const FeaturedItemsCarousel = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchItems = async () => {
            setLoading(true); setError(null);
            try {
                const fetchedItems = await getFeaturedItems();
                let displayItems = fetchedItems || [];
                // Ensure enough items for a smooth loop, duplicate if necessary
                while (displayItems.length > 0 && displayItems.length < 15) { displayItems = [...displayItems, ...fetchedItems]; }
                setItems(displayItems.slice(0, 30)); // Limit total items for performance
            } catch (err) {
                console.error("Error fetching featured items for landing page:", err);
                setError('Could not load example items.');
            } finally { setLoading(false); }
        };
        fetchItems();
    }, []);

    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

    if (loading) { return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150, my: 2 }}><CircularProgress size={30} /><Typography sx={{ ml: 2 }} variant="body1" color="text.secondary">Loading items...</Typography></Box>; }
    if (error) { return <Alert severity="warning" sx={{ my: 2, justifyContent: 'center' }}>{error}</Alert>; }
    if (!items || items.length === 0) { return null; }

    const itemWidth = 110; const itemMargin = 8; const totalItemWidth = itemWidth + itemMargin * 2;
    const animationDuration = items.length * 1.5; // Adjust speed based on number of items

    return (
        <Box ref={ref} sx={{ width: '100%', overflow: 'hidden', py: 2, position: 'relative', opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.6s ease-out, transform 0.6s ease-out', transitionDelay: '200ms', maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)', }}>
            <Box sx={{ display: 'flex', width: `calc(${totalItemWidth * items.length * 2}px)`, animation: `${scroll} ${animationDuration}s linear infinite`, '&:hover': { animationPlayState: 'paused', }, }}>
                {[...items, ...items].map((item, index) => ( // Duplicate items for seamless loop
                    <Tooltip title={item.name || 'Featured Item'} key={`${item.assetid || 'featured'}-${index}`} placement="top">
                        <Card sx={{ flexShrink: 0, width: `${itemWidth}px`, height: `${itemWidth}px`, mx: `${itemMargin}px`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 0.5, bgcolor: alpha('#000', 0.3), border: `2px solid #${item.name_color || '666'}`, borderRadius: '4px', overflow: 'hidden', transition: 'transform 0.2s ease', '&:hover': { transform: 'scale(1.05)', zIndex: 1, } }}>
                            <CardMedia component="img" sx={{ maxHeight: '65%', maxWidth: '90%', objectFit: 'contain', mb: 'auto' }} image={getSteamImageUrl(item.icon_url, '/96fx96f')} alt={item.name || '...'} loading="lazy" />
                            <Typography variant="caption" noWrap sx={{ color: `#${item.name_color || 'D2D2D2'}`, fontWeight: 'bold', width: '100%', mt: 0.5, px: 0.5, fontSize: '0.6rem', textAlign: 'center', }}>{item.name || '...'}</Typography>
                        </Card>
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
};


// --- Main Landing Page Component ---
function LandingPage() {
    const { login } = useAuth(); // Changed from openLoginModal to login

    const { ref: howItWorksRef, inView: howItWorksInView } = useInView({ triggerOnce: true, threshold: 0.1 });
    const { ref: whyChooseRef, inView: whyChooseInView } = useInView({ triggerOnce: true, threshold: 0.1 });
    const { ref: finalCtaRef, inView: finalCtaInView } = useInView({ triggerOnce: true, threshold: 0.1 });

    // --- NEW: Set Document Title ---
    useEffect(() => {
        document.title = 'SpinnerCS - Welcome';
    }, []);
    // --- END NEW ---

    // --- NEW: Data for Timeline ---
    const steps = [
        {
            icon: <AddCircleOutlineIcon />,
            iconColor: 'secondary.main',
            title: '1. Earn Spins',
            description: 'Complete easy tasks and offers from our trusted partners to get free spins for the wheel.',
        },
        {
            icon: <EmojiEventsIcon />,
            iconColor: 'primary.main',
            title: '2. Spin & Win',
            description: 'Use your earned spins on our wheel for a chance to win valuable CS skins and items instantly.',
        },
        {
            icon: <SendIcon />,
            iconColor: 'success.main',
            title: '3. Withdraw Skins',
            description: 'Easily withdraw your won items directly to your Steam account via secure Trade Offers.',
        },
    ];
    // --- END NEW DATA ---

    return (
        <Box sx={{ bgcolor: 'background.default', overflowX: 'hidden' }}>
            {/* --- Hero Section --- */}
            <HeroSection>
                <Container maxWidth="md">
                    <Typography
                        variant="h1" // Increased size
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 700,
                            letterSpacing: '1px',
                            textShadow: `
                                2px 2px 0px rgba(0,0,0,0.75),
                                -2px 2px 0px rgba(0,0,0,0.75),
                                2px -2px 0px rgba(0,0,0,0.75),
                                -2px -2px 0px rgba(0,0,0,0.75),
                                3px 3px 8px rgba(0,0,0,0.65)
                            `, // Increased outline
                            fontSize: { xs: '3rem', sm: '4rem', md: '5rem' }, // Responsive size
                        }}
                    >
                        Win CS Skins!
                    </Typography>
                    <Typography
                        variant="h4" // Increased size
                        component="p"
                        sx={(theme) => ({
                            mb: 4,
                            color: alpha(theme.palette.common.white, 0.95),
                            textShadow: `
                                1px 1px 0px rgba(0,0,0,0.7),
                                -1px 1px 0px rgba(0,0,0,0.7),
                                1px -1px 0px rgba(0,0,0,0.7),
                                -1px -1px 0px rgba(0,0,0,0.7),
                                2px 2px 5px rgba(0,0,0,0.6)
                            `, // Increased outline
                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.75rem' }, // Responsive size
                        })}
                    >
                        Complete simple tasks, earn spins, and win awesome Counter-Strike items directly to your Steam inventory.
                    </Typography>
                    {/* --- MODIFIED: Apply custom color to Hero CTA Button --- */}
                    <CtaButton
                        variant="contained"
                        sx={{
                            bgcolor: '#ED5A00', '&:hover': { bgcolor: alpha('#ED5A00', 0.85), animation: 'none', transform: 'scale(1.05)' }
                        }}
                        color="primary"
                        size="large"
                        onClick={login} // Changed from openLoginModal to login
                        endIcon={<ArrowForwardIcon />}
                    >
                        Login with Steam & Start Winning
                    </CtaButton>
                    {/* --- END MODIFICATION --- */}
                </Container>
            </HeroSection>

            {/* --- How It Works Section (MODIFIED) --- */}
            <Container ref={howItWorksRef} maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}> {/* Changed maxWidth to md */}
                <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ mb: 5, fontWeight: 600, opacity: howItWorksInView ? 1 : 0, transform: howItWorksInView ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease-out, transform 0.6s ease-out', }}>
                    How It Works
                </Typography>

                {/* --- NEW: Timeline Implementation --- */}
                <Timeline
                    position="alternate" // Alternating sides layout
                    sx={{
                        // Remove padding adjustment for alternate position if needed
                        // [`& .${timelineItemClasses.root}:before`]: {
                        //     flex: 0,
                        //     padding: 0,
                        // },
                        opacity: howItWorksInView ? 1 : 0,
                        transition: 'opacity 0.6s ease-out 0.2s', // Add slight delay
                    }}
                >
                    {steps.map((step, index) => (
                        <TimelineItem key={step.title}>
                            <TimelineSeparator>
                                <TimelineDot sx={{ bgcolor: step.iconColor, p: 1.5 }}> {/* Increased padding */}
                                    {React.cloneElement(step.icon, { fontSize: 'medium' })} {/* Ensure icon size */}
                                </TimelineDot>
                                {index < steps.length - 1 && <TimelineConnector sx={{ bgcolor: 'primary.light' }} />}
                            </TimelineSeparator>
                            <TimelineContent sx={{ py: '12px', px: 2 }}>
                                {/* Animate content */}
                                <Box sx={{
                                    opacity: howItWorksInView ? 1 : 0,
                                    transform: howItWorksInView ? 'translateY(0)' : 'translateY(20px)',
                                    transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                                    transitionDelay: `${index * 150 + 300}ms`, // Stagger animation
                                }}>
                                    <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                                        {step.title}
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary"> {/* Changed to body1 */}
                                        {step.description}
                                    </Typography>
                                </Box>
                            </TimelineContent>
                        </TimelineItem>
                    ))}
                </Timeline>
                {/* --- END NEW: Timeline Implementation --- */}

                {/* --- REMOVED: Original Grid Implementation --- */}
                {/* <Grid container spacing={4} justifyContent="center"> ... </Grid> */}

            </Container>

            {/* --- Featured Items Section (no changes) --- */}
            <Box sx={(theme) => ({ bgcolor: alpha(theme.palette.background.paper, 0.3), py: { xs: 3, sm: 5 } })}>
                <Container maxWidth="lg">
                    <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                        Current Jackpot items
                    </Typography>
                    <FeaturedItemsCarousel />
                </Container>
            </Box>

            {/* --- Features/Trust Section (no changes) --- */}
            <Box ref={whyChooseRef} sx={(theme) => ({ bgcolor: alpha(theme.palette.background.paper, 0.6), py: { xs: 4, sm: 5 } })}>
                <Container maxWidth="md">
                    <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600, mb: 4, opacity: whyChooseInView ? 1 : 0, transform: whyChooseInView ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease-out, transform 0.6s ease-out', }}>
                        Why Choose SpinnerCS?
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center" alignItems="center" sx={{ opacity: whyChooseInView ? 1 : 0, transform: whyChooseInView ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.6s ease-out, transform 0.6s ease-out', transitionDelay: '200ms', }}>
                        <Chip icon={<CheckCircleOutlineIcon />} label="100% Free to Play" color="success" variant="outlined" sx={(theme) => ({ p: 1, fontSize: '1rem', bgcolor: alpha(theme.palette.background.default, 0.3) })} />
                        <Chip icon={<CheckCircleOutlineIcon />} label="Instant Withdrawals" color="info" variant="outlined" sx={(theme) => ({ p: 1, fontSize: '1rem', bgcolor: alpha(theme.palette.background.default, 0.3) })} />
                        <Chip icon={<CheckCircleOutlineIcon />} label="Secure Steam Login" color="warning" variant="outlined" sx={(theme) => ({ p: 1, fontSize: '1rem', bgcolor: alpha(theme.palette.background.default, 0.3) })} />
                    </Stack>
                </Container>
            </Box>

            {/* --- Final CTA (no changes) --- */}
            <Container ref={finalCtaRef} maxWidth="sm" sx={{ py: { xs: 5, sm: 6 }, textAlign: 'center' }}>
                <Box sx={{ opacity: finalCtaInView ? 1 : 0, transform: finalCtaInView ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.8s ease-out, transform 0.8s ease-out', }}>
                    <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                        Ready to Get Started?
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        Join thousands of players winning free skins every day. Log in securely with your Steam account now!
                    </Typography>
                    <Button
                        // --- MODIFIED: Apply custom color to Final CTA Button ---
                        variant="contained"
                        size="large" // Keep size prop
                        onClick={login} // Changed from openLoginModal to login
                        // --- CONSOLIDATED sx PROP ---
                        sx={(theme) => ({
                            bgcolor: '#ED5A00', // Custom background color
                            padding: theme.spacing(1.5, 4), // Use theme for spacing
                            fontSize: '1.1rem', fontWeight: 'bold', borderRadius: '50px', transition: 'transform 0.2s ease',
                            '&:hover': { bgcolor: alpha('#ED5A00', 0.85), transform: 'scale(1.05)' } // Custom hover with lighter bg and scale
                        })}
                        endIcon={<ArrowForwardIcon />}
                    >
                        Login with Steam
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 3, color: 'text.secondary' }}>
                        {/* --- END MODIFICATION --- */}
                        By logging in, you agree to our{' '}
                        <Link component={RouterLink} to="/terms" underline="hover" color="inherit">
                            Terms of Service
                        </Link>
                        {' '}and{' '}
                        <Link component={RouterLink} to="/privacy" underline="hover" color="inherit">
                            Privacy Policy
                        </Link>.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}

export default LandingPage;
